import { Box, Card, Chip, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link } from 'react-router-dom'

/**
 * Centralized info section styled components
 * Used for detail views across Deals, Organizations, Contacts, Leads
 */

/**
 * Info box container with rounded corners and themed background
 */
export const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(30, 39, 53)' : '#f4f6f8',
  padding: theme.spacing(6),
  borderRadius: '25px',
}))

/**
 * Custom info box - no padding variant
 */
export const CustomInfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(30, 39, 53)' : '#f4f6f8',
  padding: theme.spacing(0),
  borderRadius: '25px',
}))

/**
 * Info box container - alternate background
 */
export const InfoBoxContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(30, 39, 53)' : 'rgb(255, 255, 255)',
  padding: theme.spacing(3),
  borderRadius: '25px',
}))

/**
 * Info row - wrapper for label/value pairs
 */
export const InfoRow = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.2),
  position: 'relative',
}))

/**
 * Info label - secondary text for labels
 */
export const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: '0.875rem',
  marginBottom: theme.spacing(0.5),
}))

/**
 * Info value wrapper - for positioning
 */
export const InfoValueWrapper = styled(Box)(() => ({
  position: 'relative',
}))

/**
 * Info value - primary text for values
 */
export const InfoValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 700,
  fontSize: '1rem',
  width: 'auto',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 'auto',
}))

/**
 * Styled link with hover underline
 */
export const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,1)' : 'rgba(0,0,0,1) !important',
  fontWeight: 700,
  fontSize: '1rem',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}))

/**
 * Styled link - blue variant for Organizations/Contacts
 */
export const StyledLinkBlue = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
  fontWeight: 700,
  fontSize: '1rem',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}))

/**
 * Underline divider for info rows
 */
export const Underline = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '1px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
}))

/**
 * City/State/Zip row layout
 */
export const CityStateZipRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  position: 'relative',
}))

/**
 * Organizations/Details card with themed border
 */
export const DetailsCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(30 43 59)' : 'rgb(255, 255, 255)',
  textAlign: 'left',
  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0,0,0,0.12)',
  borderWidth: '1px',
  borderStyle: 'solid',
  fontWeight: 'bold',
  borderRadius: '4px',
  '&:hover': {
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.32)' : 'rgba(0,0,0,0.22)',
  },
  color: theme.palette.mode === 'dark' ? 'white' : 'black',
}))

/**
 * Organization details box with themed border
 */
export const DetailsBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(30 43 59)' : 'rgb(255, 255, 255)',
  textAlign: 'left',
  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0,0,0,0.12)',
  borderWidth: '1px',
  borderStyle: 'solid',
  fontWeight: 'bold',
  borderRadius: '4px',
  '&:hover': {
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.32)' : 'rgba(0,0,0,0.22)',
  },
  color: theme.palette.mode === 'dark' ? 'white' : 'black',
}))

/**
 * Company label chip
 */
export const CompanyLabelChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgb(30 43 59)' : '#ffffff',
  textAlign: 'left',
  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0,0,0,0.12)',
  borderWidth: '1px',
  borderStyle: 'solid',
  fontWeight: 'bold',
  height: '20px',
  width: '60px',
  borderRadius: '4px',
  '&:hover': {
    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.32)' : 'rgba(0,0,0,0.22)',
  },
  color: theme.palette.mode === 'dark' ? 'white' : 'black',
}))

/**
 * Dialog icon (circular image)
 */
export const DialogIcon = styled('img')({
  width: 50,
  height: 50,
  borderRadius: '50%',
})

/**
 * User image styles (for use with sx prop or style attribute)
 */
export const userImageStyles = {
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  objectFit: 'cover' as const,
}

/**
 * Person icon styles (for use with sx prop)
 */
export const personIconStyles = {
  color: 'rgb(255, 123, 187)',
}

/**
 * Icon validated styles (for use with sx prop)
 */
export const iconValidatedStyles = (theme: { palette: { mode: string } }) => ({
  height: 18,
  width: 18,
  color: theme.palette.mode === 'dark' ? 'rgb(25, 117, 255)' : 'rgba(0,0,0,0.8)',
})

/**
 * Icon invalidated styles (for use with sx prop)
 */
export const iconInvalidatedStyles = (theme: { palette: { mode: string } }) => ({
  height: 18,
  width: 18,
  color: theme.palette.mode === 'dark' ? 'rgb(255,255,255,0.4)' : 'rgba(0,0,0,0.8)',
})

/**
 * List item style (for use with sx prop)
 */
export const listItemStyle = (fontsize?: number) => ({
  fontSize: fontsize ?? 15,
  p: 0,
})
